import { useReadContracts } from "wagmi";
import { type Address } from "viem";
import { ERC721DropAbi } from "@atelier/abis";

/** Wagmi tuple types get unwieldy at this fan-out — we cast to a flat shape. */
type SimpleRead = { status: "success"; result: unknown } | { status: "failure" };

/**
 * Pulls the on-chain state a public mint page needs in one batched call:
 * collection name + supply/price/window flags + presale config (publicMintAt
 * + authorizedSigner) + reveal flag + ERC2981 royalty fallback.
 *
 * Returns `null` for individual fields when the call failed (e.g. legacy
 * contracts that predate a given function), so callers can default safely.
 */
export type DropReads = {
  nameOn?: string;
  maxSupply: bigint;
  totalMinted: bigint;
  priceWei: bigint;
  paused: boolean;
  mintStart: number;
  mintEnd: number;
  publicMintAt: number;
  onChainSigner?: Address;
  revealed: boolean;
  royaltyBps: number;
  refetch: () => unknown;
};

export function useDropReads({
  address,
  chainId,
  enabled,
}: {
  address: Address;
  chainId: number;
  enabled: boolean;
}): DropReads {
  // No `as const` on the contracts array — the inferred tuple gets so deep
  // tsc throws "Type instantiation excessively deep" past ~10 calls.
  const reads = useReadContracts({
    allowFailure: true,
    contracts: enabled
      ? [
          { address, abi: ERC721DropAbi, functionName: "name", chainId },
          { address, abi: ERC721DropAbi, functionName: "symbol", chainId },
          { address, abi: ERC721DropAbi, functionName: "maxSupply", chainId },
          { address, abi: ERC721DropAbi, functionName: "totalMinted", chainId },
          { address, abi: ERC721DropAbi, functionName: "mintPriceWei", chainId },
          { address, abi: ERC721DropAbi, functionName: "paused", chainId },
          { address, abi: ERC721DropAbi, functionName: "mintStart", chainId },
          { address, abi: ERC721DropAbi, functionName: "mintEnd", chainId },
          { address, abi: ERC721DropAbi, functionName: "publicMintAt", chainId },
          { address, abi: ERC721DropAbi, functionName: "authorizedSigner", chainId },
          { address, abi: ERC721DropAbi, functionName: "revealed", chainId },
          { address, abi: ERC721DropAbi, functionName: "royaltyInfo", args: [1n, 10000n], chainId },
        ]
      : [],
    query: { enabled, refetchInterval: 15_000 },
  });

  const data = (reads.data as unknown) as readonly SimpleRead[] | undefined;
  const at = (i: number): SimpleRead | undefined => data?.[i];

  const r0 = at(0);
  const r2 = at(2);
  const r3 = at(3);
  const r4 = at(4);
  const r5 = at(5);
  const r6 = at(6);
  const r7 = at(7);
  const r8 = at(8);
  const r9 = at(9);
  const r10 = at(10);
  const r11 = at(11);

  const royaltyTuple =
    r11?.status === "success" ? (r11.result as readonly [Address, bigint]) : null;
  // royaltyInfo on a 10_000 sale price returns the bps directly.
  const royaltyBps = royaltyTuple ? Number(royaltyTuple[1]) : 0;

  return {
    ...(r0?.status === "success" ? { nameOn: r0.result as string } : {}),
    maxSupply: r2?.status === "success" ? (r2.result as bigint) : 0n,
    totalMinted: r3?.status === "success" ? (r3.result as bigint) : 0n,
    priceWei: r4?.status === "success" ? (r4.result as bigint) : 0n,
    paused: r5?.status === "success" ? (r5.result as boolean) : false,
    mintStart: r6?.status === "success" ? Number(r6.result as bigint) : 0,
    mintEnd: r7?.status === "success" ? Number(r7.result as bigint) : 0,
    publicMintAt: r8?.status === "success" ? Number(r8.result as bigint) : 0,
    ...(r9?.status === "success" ? { onChainSigner: r9.result as Address } : {}),
    revealed: r10?.status === "success" ? (r10.result as boolean) : true,
    royaltyBps,
    refetch: reads.refetch,
  };
}
